import EventBus from "../utils/eventBus";

const BILLING_COUNTRY_SELECT_ID = "#addressesForm_billingAddress_iso2_code";
const SHIPPING_COUNTRY_SELECT_ID = "#addressesForm_shippingAddress_iso2_code";
const SAME_AS_BILLING_VALUE = "deliveryAddressSameAsBilling";
const DELIVER_ADDRESS_NOT_EQUAL_VALUE = "deliveryAddressNotEqual";
const SWITCHER_EVENT_NAME = "address-switcher-";

type FormElements = {
    sameAsBillingRadioButton: HTMLInputElement;
    differentAddressRadioButton: HTMLInputElement;
    billingAddressCountrySelect: HTMLSelectElement;
};

export default class BillingAddressCountrySelectHandler {
    public static init(eventBus: EventBus) {
        // trying to find all the needed HTML elements in the DOM
        const billingAddressCountrySelect =
            document.querySelector<HTMLSelectElement>(
                BILLING_COUNTRY_SELECT_ID,
            );

        const sameAsBillingRadioButton = this.getRadioButtonByValue(
            SAME_AS_BILLING_VALUE,
        );
        const differentAddressRadioButton = this.getRadioButtonByValue(
            DELIVER_ADDRESS_NOT_EQUAL_VALUE,
        );

        const validDeliveryCountiesIso2Codes =
            this.getValidDeliveryCountiresIso2Codes();

        // if any of the form elements are not in the DOM, we assume that we're
        // not on thes second checkout step, hence we don't do anything here
        if (
            !billingAddressCountrySelect ||
            !sameAsBillingRadioButton ||
            !differentAddressRadioButton
        ) {
            return;
        }

        new BillingAddressCountrySelect(
            {
                billingAddressCountrySelect,
                differentAddressRadioButton,
                sameAsBillingRadioButton,
            },
            validDeliveryCountiesIso2Codes,
            eventBus,
        );
    }

    public static getValidDeliveryCountiresIso2Codes() {
        const shippingAddressCountrySelect =
            document.querySelector<HTMLSelectElement>(
                SHIPPING_COUNTRY_SELECT_ID,
            );

        if (!shippingAddressCountrySelect) {
            return [];
        }

        return Array.from(shippingAddressCountrySelect.options).reduce<
            string[]
        >((accIsoCodes, currOption) => {
            // the first option is an empty option with an empty string value
            // we want to filter that one out
            if (currOption.value === "") {
                return accIsoCodes;
            }

            return [...accIsoCodes, currOption.value];
        }, []);
    }

    /**
     * This will look up a radio button by it's value
     */
    private static getRadioButtonByValue(value: string) {
        const element = document.querySelector<HTMLInputElement>(
            `[value="${value}"]`,
        );

        return element;
    }
}

class BillingAddressCountrySelect {
    private formElements: FormElements;
    private validDeliveryCountiesIso2Codes: string[];
    private eventBus: EventBus;

    constructor(
        formElements: FormElements,
        validDeliveryCountiesIso2Codes: string[],
        eventBus: EventBus,
    ) {
        this.formElements = formElements;
        this.eventBus = eventBus;
        this.validDeliveryCountiesIso2Codes = validDeliveryCountiesIso2Codes;

        this.formElements.billingAddressCountrySelect.addEventListener(
            "change",
            this.onChange.bind(this),
        );

        // initially check a preselected country
        this.handleSelectedCountry(
            formElements.billingAddressCountrySelect.value,
        );
    }

    private isBillingCountryVaildDeliveryDestination(iso2Code: string) {
        return (
            // if no country has been selected yet, we don't need to disable anything
            // that would be the case for guest users
            iso2Code === "" ||
            this.validDeliveryCountiesIso2Codes.includes(iso2Code)
        );
    }

    private handleBillingCountryIsInvalidShippingCountry() {
        this.formElements.sameAsBillingRadioButton.disabled = true;

        // if the same as delivery address button isn't checked, we're done here
        if (!this.formElements.sameAsBillingRadioButton.checked) {
            return;
        }

        // uncheck and disable same as billing address
        this.formElements.sameAsBillingRadioButton.checked = false;

        // check different address radio button
        this.formElements.differentAddressRadioButton.checked = true;

        // this will trigger the dataSwitcher to show the deliver address form
        this.eventBus.publish("contentSwitch:change:" + SWITCHER_EVENT_NAME, {
            target: `${SWITCHER_EVENT_NAME}${DELIVER_ADDRESS_NOT_EQUAL_VALUE}`,
            trigger: this.formElements.differentAddressRadioButton,
            value: DELIVER_ADDRESS_NOT_EQUAL_VALUE,
        });
    }

    private handleBillingCountryIsValidShippingCountry() {
        if (!this.formElements.sameAsBillingRadioButton.disabled) {
            return;
        }

        this.formElements.sameAsBillingRadioButton.disabled = false;
    }

    private handleSelectedCountry(iso2Code: string) {
        if (!this.isBillingCountryVaildDeliveryDestination(iso2Code)) {
            this.handleBillingCountryIsInvalidShippingCountry();

            return;
        }

        this.handleBillingCountryIsValidShippingCountry();
    }

    private onChange(event: Event) {
        const currentTarget = event.currentTarget as HTMLSelectElement;
        const newValue = currentTarget.value;
        this.handleSelectedCountry(newValue);
    }
}
